import React, { useCallback, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const focusableSelector = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

const overlayStack = [];
let originalBodyOverflow = '';

const getTopOverlay = () => overlayStack[overlayStack.length - 1];

export default function RestaurantOverlay({
  children,
  title,
  onClose,
  className = '',
  labelledBy,
  closeLabel = 'Cerrar'
}) {
  const panelRef = useRef(null);
  const overlayIdRef = useRef(Symbol('restaurant-overlay'));
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const requestClose = useCallback(() => {
    if (getTopOverlay()?.id === overlayIdRef.current) {
      onCloseRef.current();
    }
  }, []);

  useEffect(() => {
    const id = overlayIdRef.current;
    const previousFocus = document.activeElement;
    if (overlayStack.length === 0) {
      originalBodyOverflow = document.body.style.overflow;
    }
    document.body.style.overflow = 'hidden';
    const panel = panelRef.current;
    const entry = { id, panel, previousFocus };
    overlayStack.push(entry);
    const firstFocusable = panel?.querySelector(focusableSelector);
    (firstFocusable || panel)?.focus();

    const handleKeyDown = (event) => {
      if (getTopOverlay()?.id !== id) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const focusable = [...panel.querySelectorAll(focusableSelector)];
      if (!focusable.length) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const index = overlayStack.findIndex((item) => item.id === id);
      const wasTop = index === overlayStack.length - 1;
      if (index >= 0) overlayStack.splice(index, 1);
      document.body.style.overflow = overlayStack.length > 0 ? 'hidden' : originalBodyOverflow;
      if (wasTop) {
        const focusTarget = previousFocus?.isConnected
          ? previousFocus
          : getTopOverlay()?.panel;
        focusTarget?.focus?.();
      }
    };
  }, []);

  return (
    <div className="rc-overlay" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) requestClose();
    }}>
      <section
        ref={panelRef}
        className={`rc-overlay__panel ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={labelledBy ? undefined : title}
        aria-labelledby={labelledBy}
        tabIndex={-1}
      >
        <button type="button" className="rc-icon-button rc-overlay__close" onClick={requestClose} aria-label={closeLabel}>
          <X aria-hidden="true" />
        </button>
        {children}
      </section>
    </div>
  );
}
